import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Owner from '@ember/owner';

const STORAGE_KEY = 'tio-nav-preference';

export interface NavPreferenceData {
  useMegaMenu: boolean;
}

/**
 * NavPreferenceService
 *
 * Manages user navigation preferences, persisted to localStorage.
 * Allows users to opt-in to new navigation experiences like the mega-menu.
 *
 * @example
 * ```ts
 * @service declare navPreference: NavPreferenceService;
 *
 * // Check preference
 * if (this.navPreference.useMegaMenu) {
 *   // Render mega-menu
 * }
 *
 * // Set preference via setter
 * this.navPreference.useMegaMenu = true;
 *
 * // Or toggle
 * this.navPreference.toggleMegaMenu();
 * ```
 */
export default class NavPreferenceService extends Service {
  @tracked private _useMegaMenu = false;

  constructor(owner: Owner) {
    super(owner);
    this.loadFromStorage();
  }

  /**
   * Whether the user prefers the mega-menu navigation style.
   * Setting this value automatically persists to localStorage.
   */
  get useMegaMenu(): boolean {
    return this._useMegaMenu;
  }

  set useMegaMenu(value: boolean) {
    this._useMegaMenu = value;
    this.saveToStorage();
  }

  /**
   * Toggle the mega-menu preference
   */
  toggleMegaMenu() {
    this.useMegaMenu = !this._useMegaMenu;
  }

  /**
   * Reset to default preferences
   */
  reset() {
    this._useMegaMenu = false;
    this.saveToStorage();
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored) as NavPreferenceData | null;
        this._useMegaMenu = data?.useMegaMenu ?? false;
      }
    } catch {
      // Ignore parse errors, use defaults
      this._useMegaMenu = false;
    }
  }

  private saveToStorage() {
    try {
      const data: NavPreferenceData = {
        useMegaMenu: this._useMegaMenu,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignore storage errors (e.g., private browsing)
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'nav-preference': NavPreferenceService;
  }
}
